/**
 * The /app layout's frame (mission 6 WP05 T031, T032; FR-003, FR-004, NF.8,
 * C-008, C-013, C-014, NFR-008; research §1.5; style guide §5.1).
 *
 * Every /app/* screen mounts here, inside AuthGate (routes.tsx). DesktopOnly
 * wraps the whole shell once: below 768 px only its notice renders, never the
 * rail beside it (style guide §5.2).
 *
 * - The rail (220 px, fixed): the wordmark, Runs · New run · Rule catalog ·
 *   Settings with exactly one current item (`activeNav`; NavLink's prefix match
 *   would mark Runs and New run together on /app/runs/new), and the footer from
 *   the tenant: legal name, contact email in place of "user · role" (one
 *   identity per tenant, C-008). Help · Docs · Status are absent until their
 *   destinations exist (owner decision 2026-09-26; C-013; deferred to mission
 *   11, public surfaces). The Codes checklist is `v1.x` and not rendered (C-013).
 * - The top bar (56 px, sticky): the breadcrumb; the plan label, the credit chip
 *   and the avatar on the right (mission 9 WP14 T086; FR-015; U13).
 * - The billing banner (mission 9 WP14 T085; FR-015, FR-023; U12) heads `<main>`
 *   above every screen, and is suppressed on the billing page.
 * - One `<main>` with 32 px padding around the screen, and no heading of its
 *   own: every screen brings its one `h1`. The /app errorElement passes its
 *   page as `children` in place of the outlet, so a render error keeps the
 *   shell (mission 6 review follow-up 23, A-6).
 *
 * The column clears the fixed rail with `pl-[var(--rail-width)]` and keeps
 * `min-w-0`, so 1280 px has no horizontal page scroll (NFR-008), and WP03's
 * Sheet scrim (`top-[var(--topbar-height)]`, `left-[var(--rail-width)]`)
 * covers the content area only. No shadow on the rail or the top bar (§11).
 */
import type { ReactNode } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'

import { APP_HOME, useTenant } from '@/components/trellis/AuthGate'
import { DesktopOnly } from '@/components/trellis/DesktopOnly'
import { cn } from '@/components/ui/cn'
import { copyText } from '@/lib/provisional-copy'
import { AvatarMenu } from './shell/AvatarMenu'
import { BillingBanner } from './shell/BillingBanner'
import { Breadcrumb } from './shell/Breadcrumb'
import { CreditChip } from './shell/CreditChip'
import { PlanLabel } from './shell/PlanLabel'

export type NavKey = 'runs' | 'new' | 'rules' | 'settings'

/** The nav labels are FR-003's, the same words as the register 17 crumbs. */
const NAV: ReadonlyArray<{ key: NavKey; to: string; label: string }> = [
  { key: 'runs', to: '/app/runs', label: copyText('crumb.runs') },
  { key: 'new', to: '/app/runs/new', label: copyText('crumb.newRun') },
  { key: 'rules', to: '/app/rules', label: copyText('crumb.ruleCatalog') },
  { key: 'settings', to: '/app/settings/organization', label: copyText('crumb.settings') },
]

const within = (pathname: string, root: string) => pathname === root || pathname.startsWith(`${root}/`)

/** The one current rail item for a pathname, or none outside the four sections. */
export function activeNav(pathname: string): NavKey | null {
  if (within(pathname, '/app/runs/new')) return 'new'
  if (within(pathname, '/app/runs')) return 'runs'
  if (within(pathname, '/app/rules')) return 'rules'
  if (within(pathname, '/app/settings')) return 'settings'
  return null
}

export default function AppShell({ children }: { children?: ReactNode } = {}) {
  const { pathname } = useLocation()
  const { data: tenant } = useTenant()
  const active = activeNav(pathname)

  return (
    <DesktopOnly>
      <div className="min-h-screen bg-white">
        <aside className="fixed inset-y-0 left-0 flex w-[var(--rail-width)] flex-col border-r border-navy-200 bg-navy-50">
          <Link
            to={APP_HOME}
            className="flex h-[76px] flex-col justify-center px-6 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
          >
            <span className="text-h3 text-navy-950">Trellis</span>
            <span className="text-[10px] leading-[14px] text-navy-600">by Bloom Credit</span>
          </Link>

          <nav aria-label="Primary">
            <ul className="flex flex-col gap-1">
              {NAV.map((item) => {
                const current = item.key === active
                return (
                  <li key={item.key}>
                    <Link
                      to={item.to}
                      aria-current={current ? 'page' : undefined}
                      className={cn(
                        'mx-3 flex h-10 items-center rounded-md px-3 text-body-sm font-medium text-navy-700 hover:bg-navy-100 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]',
                        current &&
                          'bg-[var(--color-accent-50)] text-navy-950 [box-shadow:inset_3px_0_0_var(--color-accent)] hover:bg-[var(--color-accent-50)]',
                      )}
                    >
                      {item.label}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>

          <footer className="mt-auto border-t border-navy-200 px-6 py-4">
            {tenant ? (
              <>
                <p className="truncate text-caption font-semibold text-navy-950">{tenant.legal_name}</p>
                <p className="truncate text-[11px] leading-4 text-navy-600">{tenant.contact_email}</p>
              </>
            ) : null}
          </footer>
        </aside>

        <div className="flex min-w-0 flex-col pl-[var(--rail-width)]">
          <header className="sticky top-0 z-10 flex h-[var(--topbar-height)] items-center justify-between border-b border-navy-200 bg-white px-[var(--content-padding)]">
            <Breadcrumb />
            <div className="ml-auto flex items-center gap-4">
              <PlanLabel />
              <CreditChip />
              <AvatarMenu />
            </div>
          </header>
          <main className="min-w-0 p-[var(--content-padding)]">
            <BillingBanner />
            {children ?? <Outlet />}
          </main>
        </div>
      </div>
    </DesktopOnly>
  )
}
